import { Controller } from "@hotwired/stimulus"
import { fetchWithCsrf } from "../util/common"

// Connects to the data-controller="uploader"
export default class extends Controller {
  static targets = ["fileInput"]
  static values = {
    url: String
  }

  static allowedContentTypes = ["image/jpeg", "image/jpg", "image/png", "video/mp4"]
  static VIDEO_CONTENT_TYPE = "video/mp4"
  static DEFAULT_UPLOAD_ERROR = "Upload failed."

  selectedFiles = []
  addMoreMode = false
  errorMessage = null
  isSending = false

  connect() {
    this.handleConfirm = this.handleConfirm.bind(this)
    this.handleCancel = this.handleCancel.bind(this)
    this.handleAddMore = this.handleAddMore.bind(this)
    this.handleRemoveFile = this.handleRemoveFile.bind(this)
    this.handleCaptionChanged = this.handleCaptionChanged.bind(this)

    document.addEventListener("uploader-modal:confirm", this.handleConfirm)
    document.addEventListener("uploader-modal:cancel", this.handleCancel)
    document.addEventListener("uploader-modal:add-more", this.handleAddMore)
    document.addEventListener("uploader-modal:remove-file", this.handleRemoveFile)
    document.addEventListener("uploader-modal:caption-changed", this.handleCaptionChanged)
  }

  disconnect() {
    document.removeEventListener("uploader-modal:confirm", this.handleConfirm)
    document.removeEventListener("uploader-modal:cancel", this.handleCancel)
    document.removeEventListener("uploader-modal:add-more", this.handleAddMore)
    document.removeEventListener("uploader-modal:remove-file", this.handleRemoveFile)
    document.removeEventListener("uploader-modal:caption-changed", this.handleCaptionChanged)

    this.clearFiles()
  }

  handleFiles(event) {
    if (!this.addMoreMode) {
      this.clearFiles()
    }
    this.addMoreMode = false

    const rejectedFileNames = []

    Array.from(event.target.files).forEach(file => {
      if (this.isAllowed(file)) {
        this.selectedFiles.push(this.buildSelectedFile(file))
      } else {
        rejectedFileNames.push(file.name)
      }
    })

    this.errorMessage = this.rejectionMessage(rejectedFileNames)
    this.fileInputTarget.value = ""

    this.pushPreviews()
    this.pushFlags()
    this.emit("uploader:open")
  }

  handleConfirm() {
    this.sendFiles()
  }

  handleCancel() {
    this.reset()
  }

  handleAddMore() {
    this.addMoreMode = true
    this.fileInputTarget.click()
  }

  handleRemoveFile(event) {
    this.removeFile(event.detail.index)
  }

  handleCaptionChanged(event) {
    const selectedFile = this.selectedFiles[event.detail.index]
    if (selectedFile) {
      selectedFile.caption = this.cleanCaption(event.detail.caption)
    }
  }

  removeFile(index) {
    const [ removedFile ] = this.selectedFiles.splice(index, 1)
    if (removedFile) {
      URL.revokeObjectURL(removedFile.previewUrl)
    }

    this.pushPreviews()
    this.pushFlags()
  }

  async sendFiles() {
    if (!this.urlValue || this.selectedFiles.length === 0) return
    if (this.isSending) return

    this.isSending = true
    this.pushFlags()

    try {
      const response = await fetchWithCsrf(this.urlValue, {
        method: 'POST',
        body: this.buildFormData(),
        headers: {
          'Accept': 'text/vnd.turbo-stream.html'
        },
      })

      if (!response.ok) {
        this.failWith(await this.readErrorText(response))
        return
      }

      const html = await response.text()
      this.emit("uploader:close")
      Turbo.renderStreamMessage(html)
    } catch (error) {
      this.failWith(`Error uploading files: ${error.message}`)
    }
  }

  failWith(message) {
    this.isSending = false
    this.errorMessage = message
    this.pushFlags()
  }

  reset() {
    this.clearFiles()
    this.errorMessage = null
    this.isSending = false
  }

  clearFiles() {
    this.selectedFiles.forEach(selectedFile => URL.revokeObjectURL(selectedFile.previewUrl))
    this.selectedFiles = []
  }

  pushPreviews() {
    this.emit("uploader:previews-changed", {
      files: this.selectedFiles.map(selectedFile => ({
        previewUrl: selectedFile.previewUrl,
        isVideo: selectedFile.isVideo,
        caption: selectedFile.caption
      }))
    })
  }

  pushFlags() {
    this.emit("uploader:flags-changed", {
      errorMessage: this.errorMessage,
      canSend: this.canSend
    })
  }

  get canSend() {
    return this.selectedFiles.length > 0 && !this.isSending
  }

  buildFormData() {
    const formData = new FormData()

    this.selectedFiles.forEach(selectedFile => {
      formData.append('files[][file]', selectedFile.file)
      formData.append('files[][caption]', this.cleanCaption(selectedFile.caption))
    })

    return formData
  }

  buildSelectedFile(file) {
    return {
      file,
      caption: "",
      previewUrl: URL.createObjectURL(file),
      isVideo: file.type === this.constructor.VIDEO_CONTENT_TYPE
    }
  }

  isAllowed(file) {
    return this.constructor.allowedContentTypes.includes(file.type)
  }

  rejectionMessage(rejectedFileNames) {
    if (rejectedFileNames.length === 0) return null

    return `Only JPEG, JPG, PNG images and MP4 videos are allowed. Rejected files: ${rejectedFileNames.join(', ')}`
  }

  async readErrorText(response) {
    try {
      return await response.text()
    } catch {
      return this.constructor.DEFAULT_UPLOAD_ERROR
    }
  }

  cleanCaption(text) {
    return (text || "").replace(/\r/g, "")
  }

  emit(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { detail }))
  }
}
